// =============================================================================
// app/src/lib/compartilhar.ts — mandar ARQUIVOS pelo "Compartilhar" do celular.
//
// Nasceu com a lista de contas a pagar (22/09/2026): o dono toca, abre a folha
// de compartilhar do iPhone, escolhe WhatsApp e o pai. É a primeira vez que o
// sistema compartilha arquivo — até aqui, PDF saía por <a download>.
//
// TRÊS REGRAS QUE O IPHONE IMPÕE (Web Share nível 2, Safari/PWA):
//
//   1. share() tem de ser chamado DENTRO do toque, sem nenhum `await` antes:
//      o Safari dá alguns segundos de "ativação" ao gesto, e uma ida à rede no
//      meio faz ele recusar (NotAllowedError). Por isso o arquivo fica pronto
//      ANTES do toque, e compartilharArquivos chama share() na primeira linha.
//   2. Só `files`, sem texto nem título junto: com texto, o WhatsApp já
//      descartou o arquivo e mandou só a frase.
//   3. O que prende o app instalado na tela de início NÃO é baixar: é
//      NAVEGAR para o arquivo. Link sem o atributo `download` (ou com
//      target=_blank, window.open, outro domínio) abre o QuickLook, que em
//      tela cheia não tem botão de voltar — o bug 236943 do WebKit, movido
//      para o Radar fechado da Apple em 2022 e sem correção pública. Com
//      `download="nome"` e um blob da MESMA ORIGEM, o Safari entra em modo
//      download e a página fica de pé. É o que baixarArquivo faz.
//      `data:` URL não serve: no iPhone ela falha em SILÊNCIO (nada é salvo).
//
// E BAIXAR VÁRIOS de uma vez o navegador bloqueia: a partir do 2º arquivo do
// mesmo toque, o Chrome pede permissão. Por isso baixarVarios espaça os
// downloads — ver a constante abaixo.
// =============================================================================

export type ResultadoDoEnvio = 'enviado' | 'cancelado' | 'toque-de-novo' | 'falhou';

/** O pedaço de `navigator` que isto usa — injetável para os testes. */
export interface Navegador {
  share?: (dados: ShareData) => Promise<void>;
  canShare?: (dados?: ShareData) => boolean;
}

/** O aparelho sabe compartilhar ESTES arquivos? */
export function podeCompartilhar(arquivos: File[], nav: Navegador = navigator): boolean {
  if (!arquivos.length || typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return false;
  try {
    return nav.canShare({ files: arquivos });
  } catch {
    return false;
  }
}

/**
 * Abre o Compartilhar com os arquivos. Chamar DIRETO no onClick, com os
 * arquivos já prontos — share() roda antes de qualquer outra coisa.
 */
export function compartilharArquivos(arquivos: File[], nav: Navegador = navigator): Promise<ResultadoDoEnvio> {
  let promessa: Promise<void>;
  try {
    // Nada antes desta linha: ver a regra 1 no topo.
    promessa = nav.share!({ files: arquivos });
  } catch (e) {
    return Promise.resolve(traduzir(e));
  }
  return promessa.then(() => 'enviado' as const, traduzir);
}

function traduzir(e: unknown): ResultadoDoEnvio {
  const nome = (e as { name?: string } | null)?.name;
  // Fechar a folha sem escolher ninguém é resposta normal, não erro.
  if (nome === 'AbortError') return 'cancelado';
  // O gesto "venceu" (ou outra folha estava aberta): um toque novo resolve.
  if (nome === 'NotAllowedError' || nome === 'InvalidStateError') return 'toque-de-novo';
  return 'falhou';
}

/** O sistema está aberto pelo ícone da tela de início (PWA instalado)? */
export function estaInstalado(
  win: { matchMedia?: Window['matchMedia']; navigator?: object } = window,
): boolean {
  try {
    if (win.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch { /* navegador sem matchMedia: segue */ }
  // navigator.standalone só existe no Safari do iPhone (fora do tipo do DOM).
  return (win.navigator as { standalone?: boolean } | undefined)?.standalone === true;
}

/**
 * Baixa o arquivo para o aparelho (PC, Android, iPhone — inclusive com o
 * sistema aberto pelo ícone).
 *
 * Três cuidados que o iPhone exige (e que também não atrapalham o resto):
 * blob de object URL, nunca `data:`; o atributo `download`, que é o que
 * evita a navegação; e a âncora ANEXADA ao documento antes do clique — o
 * iOS recusa clique em elemento solto. A URL vive 60 s: revogar cedo cancela
 * o download em alguns navegadores.
 */
export function baixarArquivo(arquivo: Blob, nome: string): void {
  const url = URL.createObjectURL(arquivo);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** O intervalo entre um arquivo e o próximo. */
export const ESPERA_ENTRE_DOWNLOADS = 400;

const dormir = (ms: number) => new Promise<void>((pronto) => { setTimeout(pronto, ms); });

/**
 * Baixa vários arquivos, um a um e espaçados: disparados no mesmo instante, o
 * navegador trata do 2º em diante como "download automático" e bloqueia.
 */
export async function baixarVarios(
  arquivos: ReadonlyArray<File>,
  baixar: (arquivo: Blob, nome: string) => void = baixarArquivo,
  esperar: (ms: number) => Promise<void> = dormir,
): Promise<void> {
  for (let i = 0; i < arquivos.length; i += 1) {
    if (i > 0) await esperar(ESPERA_ENTRE_DOWNLOADS);
    const a = arquivos[i]!;
    baixar(a, a.name);
  }
}
